import { OdmSettings } from '@droidmap/shared-odm-settings'
import {
  AssetSetType,
  AssetType,
  OffsetPaginationControls,
  Optional,
} from '@droidmap/shared-service-contract'

export enum TaskType {
  Photogrammetry = 'photogrammetry',
  GCPTagging = 'gcp_tagging',
}

export enum TaskStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export interface TaskInputAssetSetReference {
  id: string
  name: string
  assetSetType: AssetSetType
}

export interface TaskViewableAssetReference {
  id: string
  name: string
  assetType: AssetType
}

export interface TaskOutputAssetSetReference {
  id: string
  name: string
  assetSetType: AssetSetType
  viewableAssets: TaskViewableAssetReference[]
}

export interface Task {
  id: string

  /**
   * The id of the project that this task belongs to
   */
  projectId: string

  /**
   * The name of the task
   */
  name: string

  /**
   * The type of the task
   */
  type: TaskType

  /**
   * The status of the task
   * @default PENDING
   */
  status: TaskStatus

  /**
   * The id of the reference to the task in the external service
   */
  referenceId?: string

  /**
   * Input asset set references associated with this task
   */
  inputAssetSetReferences?: TaskInputAssetSetReference[]

  /**
   * Output asset set references associated with this task
   */
  outputAssetSetReferences?: TaskOutputAssetSetReference[]

  /**
   * When the task's work first started — the first transition into
   * {@link TaskStatus.RUNNING}. Server-managed and set once: a mapping job
   * walks several `MappingJobStatus` values that all map to `RUNNING`, and a
   * spot interruption bounces it back through `PENDING`, so re-stamping this
   * on every RUNNING update would reset the clock mid-run. Absent on a task
   * that has never started (still queued, or failed before it got compute).
   *
   * Runtime is `endedAt - startedAt` — deliberately *not* derived from
   * `created`/`updated`: `created` includes however long the job sat queued
   * waiting for compute, and `updated` moves on unrelated edits like a
   * rename.
   */
  startedAt?: Date

  /**
   * When the task reached a terminal state ({@link TaskStatus.COMPLETED},
   * `FAILED` or `CANCELLED`). Server-managed and set once. Absent while the
   * task is still pending or running — that's the signal the UI branches on
   * to render live elapsed time instead of a final duration.
   */
  endedAt?: Date

  /**
   * When a user asked for this task's job to be cancelled (droidmap#2982).
   * Copied from the mapping job's own `cancelRequestedAt` when the request is
   * made, so the tasks table can keep showing "Cancelling…" across a reload
   * while the pipeline tears the job down — read it with
   * `isCancelRequestInFlight`, since a request that ran its course without
   * stopping the job can be sent again. Server-managed; absent on a task
   * nobody has tried to cancel.
   */
  cancelRequestedAt?: Date

  /**
   * Gigapixels of input drone imagery, snapshotted when the task was
   * submitted. Recorded so a runtime can be read *against the size of what it
   * processed* — a bare duration is not comparable between runs, because
   * runtime scales with input size (and, more sharply, falls into
   * settings-driven regimes — see droidmap#2476).
   *
   * Sized by the same estimator the quota gate admits on
   * (`estimateInputGigapixels`), so the figure shown next to a runtime is the
   * one the job was priced from. It can differ slightly from the pipeline's
   * own `calculateJobSettings` re-measure — see the note on
   * `estimatePhotogrammetryComputeUnits` — so treat it as the sizing axis for
   * comparison, not as an authoritative pixel count.
   *
   * Absent for tasks created before this was recorded, and for tasks whose
   * input could not be sized (e.g. imagery not yet upload-confirmed).
   */
  inputGigapixels?: number

  /**
   * Number of input images, snapshotted alongside {@link inputGigapixels}.
   * `imageCount / gigapixels` is a proxy for image density, which is what
   * decides whether a job lands in the under-matched or matcher-saturated
   * runtime regime (droidmap#2476) — so it is the second axis a runtime
   * comparison needs, not decoration.
   */
  inputImageCount?: number

  /**
   * The time the entity was created. This attribute is automatically
   * set by the dynamodb-onetable library.
   */
  created: Date

  /**
   * The user id of the user who created this entity.
   */
  createdBy: string

  /**
   * The last time this entity was modified. This attribute is automatically
   * set by the dynamodb-onetable library.
   */
  updated: Date

  /**
   * The user id of the user who last updated this entity.
   */
  updatedBy: string
}

export type CreateTaskInput = Omit<
  Task,
  | 'id'
  | 'status'
  | 'created'
  | 'createdBy'
  | 'updated'
  | 'updatedBy'
  | 'outputAssetSetReferences'
  // Server-managed: stamped from the task's own status transitions and from
  // server-side sizing of the input asset sets, never accepted from a client.
  | 'startedAt'
  | 'endedAt'
  | 'cancelRequestedAt'
  | 'inputGigapixels'
  | 'inputImageCount'
>

export type GetTaskInput = {
  taskId: string
}

export type UpdateTaskInput = Optional<
  Omit<
    Task,
    | 'id'
    | 'created'
    | 'createdBy'
    | 'updated'
    | 'updatedBy'
    | 'projectId'
    | 'referenceId'
    | 'type'
    // Derived server-side from the `status` transition in this same update —
    // see `Task.startedAt`. Accepting them as input would let a caller
    // rewrite a job's recorded runtime.
    | 'startedAt'
    | 'endedAt'
    // Set from the mapping job's cancel request, never by a client.
    | 'cancelRequestedAt'
    | 'inputGigapixels'
    | 'inputImageCount'
  >,
  'name' | 'status' | 'inputAssetSetReferences' | 'outputAssetSetReferences'
> & {
  taskId: string
}

export enum TaskDeletionScope {
  DELETE_ALL = 'DELETE_ALL',
  RETAIN_INPUT_ASSETS = 'RETAIN_INPUT_ASSETS',
  RETAIN_OUTPUT_ASSETS = 'RETAIN_OUTPUT_ASSETS',
  RETAIN_ALL_ASSETS = 'RETAIN_ALL_ASSETS',
}

export type DeleteTaskInput = {
  taskId: string
  /**
   * Controls the scope of what gets deleted along with the task
   * @default TaskDeletionScope.DELETE_ALL
   */
  deletionScope?: TaskDeletionScope
}

/**
 * Why a pre-flight task cost estimate would be rejected by the quota gate
 * (#2015). Mirrors the server's `QuotaBlockReason` (kept as a local union so
 * the contract — and the web bundle — don't pull in the server middleware).
 * Lets the confirmation UI deep-link to the right billing remedy: a
 * spend-limit raise for the cap reasons, a payment-method update for
 * `payment_failed` (#1781), a budget raise or disable for `budget_reached`
 * (the customer's own enforced budget, #1914; on a credit plan it measures
 * overage), an upgrade or the next period for `bundle_exhausted` (a Trial/Free
 * or no-card credit plan at its bundle, #3002), and choosing a plan for
 * `plan_required` (an org that has never had one, #3196).
 */
export type TaskCostEstimateReason =
  | 'plan_required'
  | 'bundle_exhausted'
  | 'trial_expired'
  | 'spend_limit_reached'
  | 'payment_failed'
  | 'budget_reached'

/**
 * Client-measured stats for input imagery that hasn't been uploaded yet
 * (#2251) — the new-upload wizard flow, where no asset set exists to size the
 * estimate from server-side. Read from the selected files' EXIF headers in
 * the browser, so the server can run the *same* estimator + quota pre-flight
 * it uses for existing asset sets.
 *
 * Untrusted, admission-only sizing: when server-side sizing is also possible
 * the gate takes the larger of the two, and the authoritative bill is always
 * metered on the job's actual runtime — an understated client figure can at
 * worst admit a job the gate would have refused, never change what it bills.
 */
export type TaskInputImageStats = {
  /** Number of images selected locally. */
  imageCount: number
  /**
   * Total megapixels (sum of width × height / 1e6) across the selected
   * images, with unknown-dimension files extrapolated conservatively.
   */
  totalMegapixels: number
}

/**
 * Input to the read-only pre-flight task cost estimate (#2015). Only
 * photogrammetry tasks carry a meaningful estimate; other types size to 0.
 * For imagery not yet uploaded, `inputImageStats` sizes the estimate from
 * client-measured file stats instead of an asset set (#2251).
 */
export type EstimateTaskInput = {
  type: TaskType
  projectId: string
  inputAssetSetReferences?: TaskInputAssetSetReference[]
  inputImageStats?: TaskInputImageStats
  /**
   * The settings the job will run with — the same `odmSettings` the matching
   * `POST /tasks` sends. The estimator prices per stage from these, so
   * omitting them previews the default-settings price, which the quota gate
   * will not honour for a tuned job (#2909).
   */
  odmSettings?: OdmSettings
}

/**
 * The pre-flight cost estimate for a mapping-job submission (#2015), returned
 * by `POST /tasks/estimate`. Computed by the *same* code path the quota gate
 * (#1711) blocks on, so the figure shown on the task-create confirmation step
 * matches the gate's decision rather than a client-side reimplementation that
 * can silently drift.
 */
export type TaskCostEstimate = {
  /** Gigapixels of input drone imagery the estimate sizes from. */
  gigapixels: number

  /** Estimated `mapping_job_compute_units` the job will bill. */
  estimatedUnits: number

  /**
   * Estimated dollar cost at the org's plan-tier charge rate. `0` on
   * `$0`-rate tiers. Coarse admission-control sizing — NOT the billed amount,
   * which is metered on actual runtime at completion.
   */
  estimatedCost: number

  /** Denomination of {@link estimatedCost} (the catalogue charge rates). */
  currency: string

  /**
   * Whether the input imagery could actually be sized. `false` when sizing
   * failed (e.g. an asset set whose imagery isn't upload-confirmed yet, so
   * the server fell open to a zero-size estimate) — distinct from a genuine
   * `0 GP` job. The UI uses this to show "size unavailable" rather than
   * mislabelling a sizing failure as "No charge". Hard credit/spend-cap
   * blocks ({@link wouldBlock}) are still reported when this is `false`.
   */
  estimateAvailable: boolean
} & (
  | {
      /**
       * Submission *would* be rejected by the quota gate. A hard 402 in
       * `enforce` mode; advisory only in `warn` mode (the job still submits).
       */
      wouldBlock: true
      /**
       * Why it would block. Discriminated on `wouldBlock` — mirroring the
       * server's `QuotaPreflightVerdict` — so a blocking estimate always
       * carries a reason to deep-link a remedy from. Consumers narrow with
       * `if (estimate.wouldBlock)` rather than guarding on `reason` and
       * silently rendering nothing when it's absent, which on a billing gate
       * means a block with no remedy offered.
       */
      reason: TaskCostEstimateReason
      /**
       * Whether `QUOTA_ENFORCEMENT_MODE` is in `enforce` for this
       * environment — i.e. whether submitting really will 402, as opposed to
       * warn-mode guidance the gate lets through anyway (#2431).
       *
       * `wouldBlock` alone is mode-*independent* (see the enforcer's
       * `QuotaPreflightVerdict`). A client that
       * gates a *submit* — rather than just rendering a warning — must
       * therefore check `wouldBlock && enforced`, or it turns a shadow-mode
       * observation into a hard block no env knob can roll back.
       */
      enforced: boolean
    }
  | { wouldBlock: false; reason?: undefined }
)

export const TASK_LIST_SORT_KEYS = [
  'name',
  'status',
  'type',
  'createdBy',
  'created',
] as const
export type TaskListSortKey = (typeof TASK_LIST_SORT_KEYS)[number]

export type ListTasksByProjectInput = {
  projectId: string
  /** Only return tasks in one of these states. Omit for every state. */
  statuses?: TaskStatus[]
} & OffsetPaginationControls<TaskListSortKey>

export type ComputeResources = {
  cpuAndMemory?: {
    vCpus: number
    memory: number
  }
  /**
   * Opt-*out* of on-demand capacity. Absent (the default) routes the job
   * to on-demand; only an explicit `false` requests the spot queue.
   *
   * Interpreted in exactly two Lambdas, which must agree —
   * `start-compute-instance.ts` picks the Batch job queue and
   * `get-compute-instance-details.ts` picks the matching compute
   * environment. `instanceTypeOverride` (benchmark runs) wins over this
   * flag in both.
   */
  useOnDemand?: boolean
}

export type CreatePhotogrammetryTaskInput = CreateTaskInput & {
  odmSettings?: OdmSettings
  boundaryAssetId?: string
  gcpAssetId?: string
  computeResourcesOverride?: ComputeResources
  /**
   * Client-measured input imagery stats (#2251), consumed only by the quota
   * pre-flight gate on `POST /tasks`: an asset set created moments earlier in
   * the new-upload flow has no cached pixel total yet, so without these the
   * gate would have nothing to size the job's estimated cost from. Not
   * persisted on the task; billing is metered on actual runtime.
   */
  inputImageStats?: TaskInputImageStats
  /**
   * Pin the resulting mapping job to a specific EC2 instance type
   * (e.g. `c7g.4xlarge`). Used for ODM compute benchmarking — leave
   * unset for normal jobs so Batch's best-fit allocator picks from the
   * standard family list.
   */
  instanceTypeOverride?: string
  /**
   * Storage backing for the EC2 working volume mounted at `/data`.
   * Defaults to `nvme`. Set to `ebs` to route the job to the
   * EBS-variant benchmark queue (2 TB gp3 attached volume) — required
   * for non-`d` instance types like `c6i.4xlarge` — or to `efs` for
   * the EFS-variant queue (One Zone EFS at `/data`, dev-only; ADR-0022
   * / droidmap#2847). Ignored when `instanceTypeOverride` is unset.
   */
  storage?: 'nvme' | 'ebs' | 'efs'
  /**
   * Run the resulting mapping job on a GPU instance. Routes to the
   * permanent GPU spot / on-demand queues (or to the matching GPU
   * benchmark queue when `instanceTypeOverride` is also set).
   * Defaults to false.
   */
  useGpu?: boolean
}

export type CreatePhotogrammetryTaskEventInput = {
  taskId: string
  inputAssetSetReferences: TaskInputAssetSetReference[]
  outputAssetSetReferences: TaskInputAssetSetReference[]
  odmSettings?: OdmSettings
  boundaryAssetId?: string
  gcpAssetId?: string
  computeResourcesOverride?: ComputeResources
  instanceTypeOverride?: string
  storage?: 'nvme' | 'ebs' | 'efs'
  useGpu?: boolean
}

export type TaskReference = {
  taskId: string
  referenceId: string
}

export type TaskDeletedEventInput = {
  taskId: string
  referenceId?: string
}

/* --------------------------- GCP Tagging Types --------------------------- */

export enum GCPWorkflowMode {
  CREATE_NEW = 'create_new',
  IMPORT_EXISTING = 'import_existing',
}

/**
 * A Ground Control Point with world coordinates
 */
export interface GCPPoint {
  id: string
  /** User-provided name, e.g., "GCP001" */
  name: string
  /** Longitude or easting coordinate */
  geoX: number
  /** Latitude or northing coordinate */
  geoY: number
  /** Elevation */
  geoZ: number
}

/**
 * A marker indicating where a GCP point appears in a specific image
 */
export interface GCPImageMarker {
  /** Reference to the GCPPoint */
  gcpPointId: string
  /** Reference to the image asset */
  imageAssetId: string
  /** Image filename */
  imageName: string
  /** Pixel X coordinate in the image */
  imageX: number
  /** Pixel Y coordinate in the image */
  imageY: number
}

/**
 * Complete GCP data including all points and their image markers
 */
export interface GCPData {
  /** EPSG code, e.g., "EPSG:4326" */
  epsg: string
  /** List of GCP points with world coordinates */
  points: GCPPoint[]
  /** List of markers showing where GCPs appear in images */
  markers: GCPImageMarker[]
}
